import React, { useState, useMemo, useRef, useEffect } from "react";
import { useCurrency } from "../lib/currency";
import { ChevronDown, Check, Search } from "lucide-react";

export default function CurrencySwitcher({ light = false }) {
  const { code, current, setCurrency, list } = useCurrency();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = useMemo(() => {
    if (!q) return list;
    const s = q.toLowerCase();
    return list.filter(c => c.code.toLowerCase().includes(s) || c.label.toLowerCase().includes(s));
  }, [q, list]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={()=>setOpen(!open)}
        className={`flex items-center gap-1.5 text-sm font-medium ${light ? "text-slate-700 hover:text-blue-600" : "text-white hover:text-blue-300"}`}
        data-testid="currency-switcher-btn"
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span>{code}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="absolute right-0 top-full pt-2 z-50">
          <div className="bg-white shadow-2xl rounded-xl border border-slate-200 py-2 w-[300px] max-h-[420px] flex flex-col" data-testid="currency-dropdown">
            <div className="px-3 pb-2 border-b border-slate-100">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input
                  autoFocus
                  value={q}
                  onChange={(e)=>setQ(e.target.value)}
                  placeholder={`Search ${list.length} currencies...`}
                  className="w-full pl-8 pr-2 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                  data-testid="currency-search-input"
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 ? (
                <div className="text-center text-slate-400 text-sm py-4">No currencies match</div>
              ) : filtered.map(c => (
                <button
                  key={c.code}
                  onClick={()=>{ setCurrency(c.code); setOpen(false); setQ(""); }}
                  data-testid={`currency-option-${c.code}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-800 hover:bg-blue-50 ${c.code === code ? "bg-blue-50" : ""}`}
                >
                  <span className="text-base">{c.flag}</span>
                  <span className="font-semibold w-12">{c.code}</span>
                  <span className="text-slate-500 flex-1 truncate">{c.label}</span>
                  {c.code === code && <Check size={14} className="text-blue-600"/>}
                </button>
              ))}
            </div>
            <div className="px-3 py-2 border-t border-slate-100 text-[10px] text-slate-400 text-center">
              Live rates · Razorpay supports {list.length}+ international currencies
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
